import { LinePick, Order, PreparationWithProducts } from '../models';
import { sanitizeBatchNumberForDisplay } from '@shared/utils/sanitizeBatchDisplay';

/** Product ids requeridos por un pedido (líneas sueltas y dentro de packs). */
function collectOrderProductIds(order: Order): Set<number> {
  const ids = new Set<number>();
  for (const line of order.lines || []) {
    if (line.pack?.lines) {
      for (const pl of line.pack.lines) {
        if (pl.product_id) ids.add(pl.product_id);
        else if (pl.product?.id) ids.add(pl.product.id);
      }
    } else if (line.product_id) {
      ids.add(line.product_id);
    } else if (line.product?.id) {
      ids.add(line.product.id);
    }
  }
  return ids;
}

/**
 * line_picks cuyo par (línea de prep, producto) corresponde a productos del pedido.
 * `stock_location_order_line_id` coincide con `preparation.lines[].id`.
 */
export function filterLinePicksForOrder(
  linePicks: LinePick[] | undefined,
  preparation: PreparationWithProducts,
  order: Order,
): LinePick[] {
  if (!linePicks?.length) return [];
  const orderProductIds = collectOrderProductIds(order);
  return linePicks.filter((pick) => {
    const prepLine = preparation.lines.find((l) => l.id === pick.stock_location_order_line_id);
    if (!prepLine) return false;
    return orderProductIds.has(pick.product_id) && prepLine.product_id === pick.product_id;
  });
}

/** Agrupa picks filtrados por product_id. */
export function groupLinePicksByProductId(picks: LinePick[]): Map<number, LinePick[]> {
  const map = new Map<number, LinePick[]>();
  for (const p of picks) {
    const list = map.get(p.product_id) ?? [];
    list.push(p);
    map.set(p.product_id, list);
  }
  return map;
}

export interface UniqueBatchOption {
  batch_id: number;
  /** null si el API no devuelve código de lote mostrable */
  batch_number: string | null;
  expiry_date: string | null;
  received_at: string | null;
  /** Suma de qty de los picks con este batch_id */
  pickedQty: number;
}

/** Opciones de lote únicas para un producto (misma API de picks). */
export function uniqueBatchesFromPicks(picks: LinePick[]): UniqueBatchOption[] {
  const byBatch = new Map<number, LinePick[]>();
  for (const p of picks) {
    const list = byBatch.get(p.batch_id) ?? [];
    list.push(p);
    byBatch.set(p.batch_id, list);
  }
  const out: UniqueBatchOption[] = [];
  for (const [batchId, list] of byBatch) {
    const first = list[0];
    out.push({
      batch_id: batchId,
      batch_number: sanitizeBatchNumberForDisplay(first.batch?.batch_number),
      expiry_date: first.batch?.expiry_date ?? null,
      received_at: first.batch?.received_at ?? null,
      pickedQty: list.reduce((s, x) => s + (x.qty || 0), 0),
    });
  }
  return out.sort((a, b) =>
    (a.batch_number || String(a.batch_id)).localeCompare(b.batch_number || String(b.batch_id), 'es', {
      numeric: true,
    }),
  );
}
